// File: frontend/src/views/private/Claims.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/apiService';
import { formatDate } from '../../utils/formatters';
import PageHeader from '../../components/layout/PageHeader';
import PageContainer from '../../components/layout/PageContainer';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import { 
  ClipboardList, Upload, AlertCircle, Loader2,
  Building2, Calendar, Car, Home, Heart, Umbrella,
  Scale, Briefcase, Shield, Hash, ChevronRight,
  Clock, FileText, Bot, User
} from 'lucide-react';
import styles from './Claims.module.css';

const Claims = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [files, setFiles] = useState([]);
  const [chats, setChats] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [error, setError] = useState(null);
  const [startingClaimForFile, setStartingClaimForFile] = useState(null);

  useEffect(() => {
    loadFiles();
    loadClaimsChats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFiles = async () => {
    setIsLoadingFiles(true);
    setError(null);

    try {
      const response = await apiService.getUserFiles({}, user.user_id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load policies');
      }

      // Only show completed policies with metadata
      const completedPolicies = (response.data.files || []).filter(
        f => f.file_status === 'completed' && f.metadata_summary
      );
      
      setFiles(completedPolicies);
    } catch (err) {
      console.error('Load files error:', err);
      setError(err.message || 'Failed to load policies');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const loadClaimsChats = async () => {
    setIsLoadingChats(true);

    try {
      const response = await apiService.getUserChats({}, user.user_id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load claims history');
      }

      // Filter only claims assistant chats
      const claimsChats = (response.data.chats || []).filter(
        chat => chat.agent_name === 'ai_allegoryinsurance_claims_assistant'
      );

      // Sort by most recent first
      claimsChats.sort((a, b) => 
        new Date(b.last_message_at || b.created_at) - new Date(a.last_message_at || a.created_at)
      );

      setChats(claimsChats);
    } catch (err) {
      console.error('Load claims chats error:', err);
      // Don't set error here, just log it
    } finally {
      setIsLoadingChats(false);
    }
  };

  const handleFileClaim = async (file) => {
    try {
      setStartingClaimForFile(file.file_id);

      // Check if claims chat already exists for this file
      if (file.claims_chat_id) {
        console.log('📂 Opening existing claims chat:', file.claims_chat_id);
        navigate(`/dashboard/chat/${file.claims_chat_id}?from=claims&fileId=${file.file_id}`);
        return;
      }

      console.log('🚀 Starting new claims chat for file:', file.file_id);
      
      const chatResponse = await apiService.startChat({
        agent_name: 'ai_allegoryinsurance_claims_assistant',
        agent_version: 'v1',
        file_id: file.file_id
      }, user.user_id);

      console.log('✅ Claims chat response:', chatResponse);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start claim');
      }

      const newChatId = chatResponse.data.chat_id;
      console.log('✅ Claims chat created:', newChatId);

      navigate(`/dashboard/chat/${newChatId}?from=claims&fileId=${file.file_id}`);

    } catch (err) {
      console.error('❌ Failed to start claim:', err);
      alert(err.message || 'Failed to start claim');
    } finally {
      setStartingClaimForFile(null);
    }
  };

  const handleOpenClaimChat = (chatId) => {
    navigate(`/dashboard/chat/${chatId}?from=claims`);
  };

  // ── Helper functions (reused from Policies.js) ──
  const getPolicyTypeDisplay = (type) => {
    const map = {
      auto: 'Auto Insurance', auto_policy: 'Auto Insurance',
      home: 'Home Insurance', homeowners: 'Homeowners Insurance',
      renters: 'Renters Insurance', health: 'Health Insurance',
      life: 'Life Insurance', umbrella: 'Umbrella Policy',
      liability: 'Liability Insurance', commercial: 'Commercial Insurance',
      business: 'Business Insurance', condo: 'Condo Insurance',
      flood: 'Flood Insurance', fire: 'Fire Insurance',
    };
    return map[(type || '').toLowerCase()] || formatType(type) || 'Insurance Policy';
  };

  const formatType = (type) => {
    if (!type) return '';
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getPolicyTypeIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('auto') || t.includes('vehicle') || t.includes('car')) return Car;
    if (t.includes('home') || t.includes('property') || t.includes('dwelling') || t.includes('condo')) return Home;
    if (t.includes('health') || t.includes('medical')) return Heart;
    if (t.includes('life')) return Heart;
    if (t.includes('umbrella')) return Umbrella;
    if (t.includes('liability')) return Scale;
    if (t.includes('business') || t.includes('commercial')) return Briefcase;
    return Shield;
  };

  const getPolicyTypeColor = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('auto') || t.includes('vehicle') || t.includes('car')) return styles.typeAuto;
    if (t.includes('home') || t.includes('property') || t.includes('dwelling') || t.includes('condo')) return styles.typeHome;
    if (t.includes('health') || t.includes('medical')) return styles.typeHealth;
    if (t.includes('life')) return styles.typeLife;
    if (t.includes('umbrella')) return styles.typeUmbrella;
    if (t.includes('liability') || t.includes('business') || t.includes('commercial')) return styles.typeBusiness;
    return styles.typeDefault;
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr, 'short');
  };

  const truncatePreview = (text, maxLength = 120) => {
    if (!text) return null;
    const clean = text
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/#{1,6}\s/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/`/g, '')
      .trim();
    if (clean.length <= maxLength) return clean;
    return clean.substring(0, maxLength).trim() + '…';
  };

  const isLoading = isLoadingFiles || isLoadingChats;

  if (isLoading) {
    return (
      <PageContainer>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader 
          title="File a Claim"
          subtitle="Start or continue your insurance claim"
          icon={<ClipboardList size={24} strokeWidth={2} />}
          showBack={true}
          backTo="/dashboard"
        />
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h2>Failed to Load Claims</h2>
            <p>{error}</p>
            <button onClick={() => { loadFiles(); loadClaimsChats(); }} className={styles.retryButton}>
              Try Again
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader 
        title="File a Claim"
        subtitle={files.length > 0 
          ? `Select a policy to file a claim${chats.length > 0 ? ` · ${chats.length} active ${chats.length === 1 ? 'claim' : 'claims'}` : ''}`
          : 'Upload a policy to start filing a claim'
        }
        icon={<ClipboardList size={24} strokeWidth={2} />}
        showBack={true}
        backTo="/dashboard"
      />

      {files.length === 0 ? (
        <div className={styles.emptyContainer}>
          <div className={styles.emptyCard}>
            <div className={styles.emptyIconWrap}>
              <ClipboardList size={48} strokeWidth={1.5} />
            </div>
            <h2>No policies uploaded</h2>
            <p>Upload your insurance policy first, then you can file a claim with AI-guided assistance</p>
            <button 
              onClick={() => navigate('/dashboard/upload')} 
              className={styles.uploadButtonLarge}
            >
              <Upload size={20} strokeWidth={2} />
              Upload Policy
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.claimsContent}>
          {/* ── Select Policy Section ── */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FileText size={16} />
              Select Policy to File Claim
            </h3>
            <div className={styles.policiesGrid}>
              {files.map((file) => {
                const policyType = file.metadata_summary?.policy_type || '';
                const TypeIcon = getPolicyTypeIcon(policyType);
                const typeColor = getPolicyTypeColor(policyType);
                const isStarting = startingClaimForFile === file.file_id;
                const hasClaim = !!file.claims_chat_id;

                return (
                  <button
                    key={file.file_id}
                    className={styles.policyCard}
                    onClick={() => handleFileClaim(file)}
                    disabled={isStarting}
                  >
                    <div className={`${styles.cardAccent} ${typeColor}`} />

                    <div className={styles.cardBody}>
                      <div className={styles.cardTopRow}>
                        <div className={`${styles.policyTypeTag} ${typeColor}`}>
                          <TypeIcon size={14} />
                          <span>{getPolicyTypeDisplay(policyType)}</span>
                        </div>
                        {hasClaim && (
                          <span className={styles.activeClaimBadge}>
                            <Clock size={11} />
                            Active
                          </span>
                        )}
                      </div>

                      <h3 className={styles.insurerName}>
                        {file.metadata_summary?.insurance_company || 'Unknown Insurer'}
                      </h3>

                      <div className={styles.cardDetails}>
                        {file.metadata_summary?.policy_number && (
                          <div className={styles.cardDetailRow}>
                            <Hash size={13} className={styles.cardDetailIcon} />
                            <span className={styles.cardDetailLabel}>Policy</span>
                            <span className={styles.cardDetailValue}>{file.metadata_summary.policy_number}</span>
                          </div>
                        )}
                      </div>

                      <div className={styles.cardBottomRow}>
                        <div className={styles.uploadDate}>
                          <span>Added {formatDate(file.created_at, 'long')}</span>
                        </div>
                        <div className={styles.viewArrow}>
                          {isStarting ? (
                            <>
                              <Loader2 size={16} className={styles.spinning} />
                              <span>Starting...</span>
                            </>
                          ) : (
                            <>
                              <span>{hasClaim ? 'Continue' : 'File Claim'}</span>
                              <ChevronRight size={16} />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Previous Claims Section ── */}
          {chats.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <Clock size={16} />
                Your Claims History
              </h3>
              <div className={styles.chatsGrid}>
                {chats.map((chat) => {
                  const totalMessages = (chat.user_message_count || 0) + (chat.assistant_message_count || 0);

                  return (
                    <button
                      key={chat.chat_id}
                      className={styles.chatCard}
                      onClick={() => handleOpenClaimChat(chat.chat_id)}
                    >
                      <div className={`${styles.cardAccent} ${styles.accentClaims}`} />

                      <div className={styles.cardBody}>
                        <div className={styles.cardTopRow}>
                          <div className={`${styles.agentTag} ${styles.agentClaims}`}>
                            <ClipboardList size={14} />
                            <span>Claims Assistant</span>
                          </div>
                          <span className={styles.timeAgo}>{getTimeAgo(chat.last_message_at || chat.created_at)}</span>
                        </div>

                        {chat.last_message_preview ? (
                          <p className={styles.messagePreview}>
                            {truncatePreview(chat.last_message_preview)}
                          </p>
                        ) : (
                          <p className={styles.messagePreviewEmpty}>
                            Claim started
                          </p>
                        )}

                        <div className={styles.cardStatsRow}>
                          <div className={styles.messageCounts}>
                            {chat.user_message_count > 0 && (
                              <div className={styles.messageCount}>
                                <User size={11} />
                                <span>{chat.user_message_count}</span>
                              </div>
                            )}
                            {chat.assistant_message_count > 0 && (
                              <div className={styles.messageCount}>
                                <Bot size={11} />
                                <span>{chat.assistant_message_count}</span>
                              </div>
                            )}
                            {totalMessages > 0 && (
                              <span className={styles.totalMessages}>
                                {totalMessages} message{totalMessages !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className={styles.viewArrow}>
                            <span>Continue</span>
                            <ChevronRight size={16} />
                          </div>
                        </div>

                        <div className={styles.cardDate}>
                          <span>Started {formatDate(chat.created_at, 'long')}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
};

export default Claims;